import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getData, getcountries } from '../../../services/admin/abstractReceipt/abReviewersServices';
import DataTable from 'react-data-table-component';
import Header from '../../../components/Layout/Header';
import Footer from '../../../components/Layout/Footer';
import Sidebar from '../../../components/Layout/Sidebar';
import Spinner from '../../../components/Spinner';

// ── Parse remarks safely ─────────────────────────────────────────────────────
const parseRemarks = (raw) => {
  if (!raw) return [];
  try {
    if (Array.isArray(raw)) return raw;
    let parsed = JSON.parse(raw);
    if (typeof parsed === 'string') parsed = JSON.parse(parsed);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// ── Format ISO → "22 Jun 2026, 10:30 AM" ────────────────────────────────────
const formatTimestamp = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const ApprovedReviewers = () => {
  const [dataList, setDataList]         = useState([]);
  const [searchText, setSearchText]     = useState('');
  const [currentPage, setCurrentPage]   = useState(1);
  const [loading, setLoading]           = useState(true);
  const [startDate, setStartDate]       = useState('');
  const [endDate, setEndDate]           = useState('');
  const [seriesFilter, setSeriesFilter] = useState('');
  const [subDomainFilter, setSubDomainFilter] = useState('');
  const [countryFilter, setCountryFilter]     = useState('');
  const [dropdownData, setDropdownData] = useState({ countries: [] });

  const fetchData = useCallback(async () => {
    try {
      const data = await getData({ approval: 1 });
      setDataList(data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, []);

  const fetchDropdownData = useCallback(async () => {
    try {
      const [country] = await Promise.all([getcountries()]);
      setDropdownData({ countries: country });
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  }, []);

  useEffect(() => {
    const fetchDataAsync = async () => {
      setLoading(true);
      await fetchData();
      await fetchDropdownData();
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setLoading(false);
    };
    fetchDataAsync();
  }, [fetchData, fetchDropdownData]);

  const handleSearchChange = (e) => setSearchText(e.target.value);
  const handlePageChange   = (page) => setCurrentPage(page);

  const getCountryName = (code) => {
    const country = dropdownData.countries.find(c => c.code === code);
    return country ? country.name : code;
  };

  const formatSeries = (series) => {
    const date = new Date(series);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const filteredData = useMemo(() => {
    const startDateObj = startDate ? new Date(startDate) : null;
    const endDateObj   = endDate   ? new Date(endDate)   : null;
    return dataList
      .filter((data) => {
        const matchesText =
          data.reviewer_name.toLowerCase().includes(searchText.toLowerCase()) ||
          data.sub_domain.toLowerCase().includes(searchText.toLowerCase());
        const date    = new Date(data.updatedAt);
        const inRange = startDateObj && endDateObj ? date >= startDateObj && date <= endDateObj : true;
        const matchSeries    = !seriesFilter    || data.series           === seriesFilter;
        const matchSubDomain = !subDomainFilter || data.sub_domain       === subDomainFilter;
        const matchCountry   = !countryFilter   || data.reviewer_country === countryFilter;
        return matchesText && inRange && matchSubDomain && matchCountry && matchSeries;
      })
      .sort((a, b) => startDate && endDate ? new Date(b.updatedAt) - new Date(a.updatedAt) : 0);
  }, [dataList, searchText, subDomainFilter, seriesFilter, countryFilter, startDate, endDate]);

  const columns = [
    { name: '#',         selector: (row, i) => (currentPage - 1) * 10 + (i + 1), sortable: false, width: '10%' },
    { name: 'SubDomain', selector: (row) => row.sub_domain,                       sortable: true,  width: '15%', wrap: true },
    { name: 'Series',    selector: (row) => formatSeries(row.series),             sortable: true,  width: '15%', wrap: true },
    { name: 'Name',      selector: (row) => row.reviewer_name,                    sortable: true,  width: '15%', wrap: true },
    { name: 'Country',   selector: (row) => getCountryName(row.reviewer_country || 'N/A'), sortable: true, width: '15%', wrap: true },
    {
      name: 'Signature',
      selector: (row) => <img src={row.reviewer_signature} width="100" alt={row.reviewer_affiliation} />,
      sortable: false, width: '15%', wrap: true,
    },
    {
      name: 'Status',
      cell: (row) => (
        <span className={`badge ${Number(row.status) === 1 ? 'bg-success' : 'bg-danger'}`}>
          {Number(row.status) === 1 ? 'Active' : 'Inactive'}
        </span>
      ),
      sortable: true, width: '10%', wrap: true,
    },
  ];

  // ── Expandable row: affiliation + remarks history ────────────────────────
  const ExpandableRow = ({ data }) => {
    const history = parseRemarks(data.remarks);
    return (
      <div className="p-3 bg-light border">
        <table className="table table-bordered mb-0">
          <tbody>
            <tr>
              <td><b>Reviewer Affiliation:</b></td>
              <td>{data.reviewer_affiliation}</td>
            </tr>
          </tbody>
        </table>

        {history.length > 0 && (
          <div className="mt-3">
            <p className="fw-semibold text-dark mb-2">
              Remarks History
              <span className="badge bg-secondary ms-2" style={{ fontSize: '0.72rem' }}>
                {history.length}
              </span>
            </p>
            <div
              className="border rounded"
              style={{ maxHeight: '200px', overflowY: 'auto', backgroundColor: '#ffffff' }}
            >
              {[...history].reverse().map((entry, idx) => (
                <div
                  key={idx}
                  className="px-3 py-2 border-bottom"
                  style={{ borderLeft: '3px solid #0d6efd' }}
                >
                  <small className="text-muted d-block mb-1">
                    🕒 {formatTimestamp(entry.timestamp)}
                  </small>
                  <p className="mb-0 text-dark" style={{ fontSize: '0.9rem' }}>
                    {entry.remark}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Header />

        {/* ── Filters ── */}
        <div className="row m-3 d-flex flex-column gap-3">
          <div className="d-flex gap-2">
            <div className="col-md-2">
              <span className="fs-5 text-dark">Series</span>
              <select className="form-control" value={seriesFilter} onChange={(e) => setSeriesFilter(e.target.value)}>
                <option value="">All Series</option>
                {Array.from(new Set(dataList.map(item => item.series))).sort((a, b) => b.localeCompare(a)).map((s, i) => (
                  <option key={i} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <span className="fs-5 text-dark">Sub Domain</span>
              <select className="form-control" value={subDomainFilter} onChange={(e) => setSubDomainFilter(e.target.value)}>
                <option value="">All Subdomains</option>
                {Array.from(new Set(dataList.map(item => item.sub_domain))).sort((a, b) => a.localeCompare(b)).map((sd) => (
                  <option key={sd} value={sd}>{sd}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <span className="fs-5 text-dark">Countries</span>
              <select className="form-control" value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)}>
                <option value="">All Countries</option>
                {dropdownData.countries.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <span className="fs-5 text-dark">Start Date</span>
              <input type="date" className="form-control" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="col-md-2">
              <span className="fs-5 text-dark">End Date</span>
              <input type="date" className="form-control" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '300px' }}>
            <Spinner />
          </div>
        ) : (
          <div>
            <div className="container-fluid p-4">
              <div className="row">
                <div className="col-8">
                  <p className="fs-4 text-dark">PDF Generation / Abstract Receipt / Reviewer / Approved Reviewers</p>
                </div>
              </div>
              <DataTable
                columns={columns}
                data={filteredData}
                pagination
                highlightOnHover
                responsive
                striped
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 20, 50, 100, 200, 500]}
                onChangePage={handlePageChange}
                subHeader
                subHeaderComponent={
                  <input className="form-control" type="text" placeholder="Search"
                    value={searchText} onChange={handleSearchChange} />
                }
                expandableRows
                expandableRowsComponent={ExpandableRow}
              />
            </div>
            <Footer />
          </div>
        )}
      </div>
    </div>
  );
};

export default ApprovedReviewers;
